
import React from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardIcon } from "@heroicons/react/24/outline"; 
import { FiChevronRight } from "react-icons/fi";
import agent1Img from "../../assets/Agent2.png"
import agent2Img from "../../assets/Agent3.png"
import agent4Img from "../../assets/Agent6.png"

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={copyToClipboard}
          className="p-1.5 rounded text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-lg leading-none">✔</span>
          ) : (
            <ClipboardIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
        {code}
      </pre>
    </div>
  );
};

export function renderNodejs() {
  return <NodejsComponent />;
}

function NodejsComponent() {
  const navigate = useNavigate();
    return (
      <div>
        <main className="transition-opacity duration-300">
          <div className="py-4">
            {/* Breadcrumbs */}
           <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
          <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
          <FiChevronRight />
          <span>Agent Setup Guide</span>
          <FiChevronRight />
          <span>Technology Agent</span>
          <FiChevronRight />
          <span className="text-[#1b140d] dark:text-white font-medium">Nodejs</span>
        </nav>
    
            {/* Intro */}
            <div className="mb-10 scroll-mt-24" id="Overview">
              <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
                Documentation
              </span>
              <h1 className="text-3xl lg:text-4xl font-extrabold text-text-main dark:text-white tracking-tight mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">Netraa </span>Node.js Agent Installation Guide
              </h1>
              <p className="text-lg text-text-muted dark:text-gray-400 leading-relaxed">
                This guide explains how to install and configure the Apmosys Backend JS Agent inside your Node.js application.
              </p>
            </div>
    
            {/* Step 1: Add Agent Package */}
            <section className="mb-10 scroll-mt-24" id="steps-to-follow">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 border border-blue-200 dark:border-blue-900/50">
                  <span className="material-symbols-outlined text-[24px]">file_download</span>
                </span>
                1. Add Agent Package to Your Project
              </h2>
              <p className="text-sm text-text-muted mb-3">
                Download the package provided in the ApMoSys share point and move the agent package file into your project directory:
              </p>
              <CodeBlock code={`apmosys-backend-js-x.x.x.tgz`} />
              <p className="text-sm text-text-muted mb-3">Example project structure:</p>
              <CodeBlock code={`my-node-app/
     ├─ src/
     ├─ package.json
     ├─ apmosys-backend-js-x.x.x.tgz`} />
            </section>
    
            {/* Step 2: Install the Agent */}
            <section className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 border border-green-200 dark:border-green-900/50">
                  <span className="material-symbols-outlined text-[24px]">install_desktop</span>
                </span>
                2. Install the Agent
              </h2>
              <p className="text-sm text-text-muted mb-3">
                Inside your Node.js project root, run:
              </p>
              <CodeBlock code={`npm install ./apmosys-backend-js-x.x.x.tgz`} />
              <p className="text-sm text-text-muted">This will install the agent locally.</p>
            </section>
    
            {/* Step 3: Usage */}
            <section className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 border border-purple-200 dark:border-purple-900/50">
                  <span className="material-symbols-outlined text-[24px]">code</span>
                </span>
                3. Usage
              </h2>
              <p className="text-sm text-text-muted mb-3">Use the correct initialization based on your module type.</p>
    
              <h3 className="text-sm font-bold text-text-main dark:text-white mb-2">For ES Modules ("type": "module")</h3>
              <CodeBlock code={`import agent from 'apmosys-backend-js';
    
    agent.default.start({
      serviceName: 'my_app',
      collectorAddress: 'netraaaop.ap2l.ai:11800'
    });`} />
    
              <h3 className="text-sm font-bold text-text-main dark:text-white mb-2">For CommonJS ("type": "commonjs" or no “type” field)</h3>
              <CodeBlock code={`const { default: agent } = require('apmosys-backend-js');
    const express = require('express');
    
    agent.start({
      serviceName: 'my_app',
      collectorAddress: 'netraaaop.ap2l.ai:11800'
    });`} />
    
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">
                <strong>Important:</strong> The agent must be initialized before your server starts so it can instrument all modules correctly.
              </p>
            </section>
    
            {/* Step 4: Start Application */}
            <section className="mb-10 scroll-mt-24" id="step-4">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 border border-cyan-200 dark:border-cyan-900/50">
                  <span className="material-symbols-outlined text-[24px]">play_arrow</span>
                </span>
                4. Start Your Application
              </h2>
              <CodeBlock code={`node app.js
    # Or
    npm start`} />
            </section>
    
            {/* Step 5: Verify Agent Connection */}
            <section className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 border border-amber-200 dark:border-amber-900/50">
                  <span className="material-symbols-outlined text-[24px]">check_circle</span>
                </span>
                5. Verify Agent Connection
              </h2>
              <p className="text-sm text-text-muted mb-3">
                The agent sends traces to the configured collector address:
              </p>
              <CodeBlock code={`collectorAddress: "localhost:11800"`} />
              <p className="text-sm text-text-muted font-bold">Done! Your Node.js application is now instrumented with the Apmosys Backend JS Agent.</p>
            </section>
                {/* Visualize Data CTA */}
                <section className="mb-10 flex justify-center">
                  <button
                    onClick={() => navigate("/homeservices")}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white
                               bg-gradient-to-r from-orange-500 to-red-600
                               hover:from-orange-600 hover:to-red-700
                               shadow-md hover:shadow-lg transition-all duration-300
                               dark:shadow-orange-900/30"
                  >
                    Visualize Data
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                </section>
          </div>
        </main>
      </div>
    );
    }